import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import HoveredCard from '../../common/card/HoveredCard';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import CartIconBadge from '../../common/cart/CartIconBadge';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { JiraServerContext } from '../jira-server/jira-server.context';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { ProductBoardColumnEntity } from './board/ProductBoardColumn';
import DesignOutputDesignInputReferenceCard from './cards/DesignOutputDesignInputReferenceCard';
import DesignOutputInfoCard from './cards/DesignOutputInfoCard';
import DesignOutputUserNeedReferenceCard from './cards/DesignOutputUserNeedReferenceCard';
import DesignOutputVerificationReferenceCard from './cards/DesignOutputVerificationReferenceCard';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import ProductsSubSectionComponentCard from './cards/ProductsSubSectionComponentCard';
import ProductsSubSectionReleaseCard from './cards/ProductsSubSectionReleaseCard';
import {
  getProductEntityName,
  isProductRelationDisabled
} from './Product.config';
import {
  useFindProductDesignOutputById,
  useFindProductDesignOutputVersion,
  useProductEntityPrefixId
} from './Product.hooks';
import {
  BaseProductDesignOutputCardProps,
  ProductDesignOutput
} from './Product.interface';
import TaskDueDateCard from '../tasks/cards/TaskDueDateCard';
import DesignOutputRiskAssessmentCard from './cards/DesignOutputRiskAssessmentCard';
import DesignOutputValidationReferenceCard from './cards/DesignOutputValidationReferenceCard';
import DesignOutputDesignSpecReferenceCard from './cards/DesignOutputDesignSpecReferenceCard';

const ProductDesignOutputDetailPage: React.FC = () => {
  const {
    productId: paramProductId = '',
    designOutputId: paramDesignOutputId = ''
  } = useParams<{
    productId: string;
    designOutputId: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const designOutputId: number = parseInt(paramDesignOutputId) || 0;
  const fetchUserNeedsRef = useRef<Function | undefined>();
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [productDesignOutput, setProductDesignOutput] = useState<
    ProductDesignOutput | undefined
  >();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useContext(JiraServerContext);
  const {
    productDesignOutput: data,
    isLoadingProductDesignOutput,
    findProductDesignOutputError,
    findProductDesignOutput
  } = useFindProductDesignOutputById(productId, designOutputId);
  const {
    productDesignOutputVersion: loadedProductDesignOutputVersion,
    findProductDesignOutputVersion
  } = useFindProductDesignOutputVersion(productId, designOutputId, version);
  const [productDesignOutputVersion, setProductDesignOutputVersion] = useState(
    loadedProductDesignOutputVersion
  );

  const title: string = useProductEntityPrefixId(
    productDesignOutput,
    ProductBoardColumnEntity.DesignOutput
  );

  const cardProps: BaseProductDesignOutputCardProps = {
    productId,
    productDesignOutput: productDesignOutput!,
    productDesignOutputVersion,
    onChange: (newProductDesignOutput: Partial<ProductDesignOutput>) => {
      if (productDesignOutput) {
        // notify task components to refresh
        productDesignOutput.task = { ...productDesignOutput.task };
        setProductDesignOutput({
          ...productDesignOutput,
          ...newProductDesignOutput
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!productDesignOutput?.task) {
      return;
    }
    Object.assign(productDesignOutput.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findProductDesignOutput();
  }, [findProductDesignOutput, productId, designOutputId]);
  useEffect(() => {
    if (version) {
      findProductDesignOutputVersion();
    } else {
      setProductDesignOutputVersion(undefined);
    }
  }, [findProductDesignOutputVersion, productId, designOutputId, version]);
  useEffect(
    () =>
      loadedProductDesignOutputVersion &&
      setProductDesignOutputVersion(loadedProductDesignOutputVersion),
    [loadedProductDesignOutputVersion]
  );

  useEffect(() => {
    data && setProductDesignOutput(data);
  }, [data]);

  useEffect(() => {
    setError(findProductDesignOutputError);
  }, [findProductDesignOutputError]);
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.DesignOutput>(
    productDesignOutput?.task
  );
  const task = productDesignOutputVersion?.task || productDesignOutput?.task;
  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to={`/portal/products/${productId}/${from}`} />
        <HeaderTitle>
          <h2>{isLoadingProductDesignOutput ? 'Loading...' : title}</h2>
          {productDesignOutput && (
            <div className='status-container'>
              <TaskStatusLabel task={productDesignOutput.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          <CartIconBadge
            taskEntity={productDesignOutput}
            onChange={productDesignOutput =>
              setProductDesignOutput(productDesignOutput as ProductDesignOutput)
            }
          />
          {productDesignOutput?.task && (
            <TaskStatusActions
              task={productDesignOutput.task}
              taskEntity={productDesignOutput}
              onChange={(task: Task) => {
                productDesignOutput.task = task;
                setProductDesignOutput({ ...productDesignOutput });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {productDesignOutput && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {productDesignOutputVersion &&
            ` from ${formatDate(productDesignOutputVersion.created)}. `}
          <Link to={`./${productDesignOutput.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {productDesignOutput && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <DesignOutputInfoCard {...cardProps} />
                </Grid>
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={productDesignOutput.task}
                      entity={{
                        title: productDesignOutput.title,
                        description: productDesignOutput.description
                      }}
                      jiraFieldColumns={
                        environment.productConfig?.designOutput
                          ?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update productDesignOutput Task jira issues on change
                        productDesignOutput.task.jiraIssues = taskJiraIssues;
                        setProductDesignOutput({ ...productDesignOutput });
                      }}
                    />
                  </Grid>
                )}
                {productDesignOutput.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={productDesignOutput.task}
                      disabled={Boolean(productDesignOutputVersion)}
                      attachments={productDesignOutputVersion?.attachments}
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
                {environment.productConfig?.designOutput
                  ?.enableRiskAssessment && (
                  <Grid item xs={12}>
                    <DesignOutputRiskAssessmentCard {...cardProps} />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {productDesignOutput.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusVersionCard
                        showCart
                        taskEntity={productDesignOutput}
                        renderContent={group =>
                          group.version !==
                            productDesignOutput.task.version && (
                            <HoveredCard
                              title={`Version ${group.version}`}
                              content='View the previous version'
                              actions={
                                <Button
                                  variant='text'
                                  color='primary'
                                  component={Link}
                                  startIcon={<Visibility />}
                                  to={`./${productDesignOutput.id}?ver=${group.version}`}
                                >
                                  View
                                </Button>
                              }
                            />
                          )
                        }
                      />
                    </Grid>
                    {task?.children && task.children.length > 0 && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          title='Stakeholders'
                          task={task}
                          disabled={
                            Boolean(version) ||
                            isFieldDisabled('assignSubTasks')
                          }
                          onError={cardProps.onError}
                          onChange={handleTaskUpdate}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <ProductsSubSectionComponentCard
                        productSubSection={productDesignOutput}
                        productId={productId}
                        readOnlyComponents={
                          productDesignOutputVersion &&
                          (productDesignOutputVersion.components || [])
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ProductsSubSectionReleaseCard
                        productId={productId}
                        productSubSection={productDesignOutput}
                        disabled={Boolean(productDesignOutputVersion)}
                        readOnlyRelease={
                          productDesignOutputVersion &&
                          (productDesignOutputVersion?.release || null)
                        }
                      />
                    </Grid>
                    {task && (
                      <Grid item xs={12}>
                        <TaskAssignCard task={task} />
                      </Grid>
                    )}
                    {productDesignOutput.task && (
                      <>
                        <Grid item xs={12}>
                          <TaskFileListCard
                            task={productDesignOutput.task}
                            disabled={Boolean(productDesignOutputVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TaskDueDateCard
                            task={productDesignOutput.task}
                            disabled={Boolean(productDesignOutputVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  <DesignOutputUserNeedReferenceCard
                    {...cardProps}
                    fetchRef={fetchUserNeedsRef}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DesignOutputDesignInputReferenceCard
                    {...cardProps}
                    onUpdate={() => {
                      fetchUserNeedsRef.current?.();
                      cardProps.onUpdate?.();
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DesignOutputVerificationReferenceCard {...cardProps} />
                </Grid>
                {!isProductRelationDisabled('designOutput', 'validations') && (
                  <Grid item xs={12}>
                    <DesignOutputValidationReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('designOutput', 'designSpecs') && (
                  <Grid item xs={12}>
                    <DesignOutputDesignSpecReferenceCard {...cardProps} />
                  </Grid>
                )}
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title={getProductEntityName('designOutput')}
                      apiPath={`products/${productId}/design-outputs`}
                      entity={productDesignOutput}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('designOutput')} details updated.
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default ProductDesignOutputDetailPage;
